/**
 * Version history for the IAP-core forms, against the generic
 * `ics_form_version` endpoints. One hook family serves the 202, 203, 205, 205A,
 * 206, and 208; the form's base id is a parameter rather than a copy of the
 * file.
 *
 * Modelled on `ics215FormVersionHooks.ts`, which these mirror down to the toast
 * and invalidation behaviour.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useRef } from 'react';
import { toast } from 'react-toastify';

import { toastConfig } from 'assets/data/config';
import config from '../../../constants/apiConfig';
import { useActualIncidentId } from './commonHooks';
import type { WalaSignature } from '../planningView/widgets/AssignmentList/ics204SignatureSlots';
import type { ActionAttachment } from '../actionTicket/actionTicketModel';

/**
 * The frozen copy of the boxes the form prints. Deliberately narrow: signing
 * one form must not freeze the `ics_incident_info` fields another card owns, so
 * the server snapshots only this form's columns.
 */
export type IcsFormVersionPayload = {
  fields: Record<string, unknown>;
};

/** A signature box on the printed form, labelled from its descriptor. */
export type IcsFormSignatureSlot = {
  id: string;
  label: string;
  role: string;
  /**
   * Whether the box takes one signature per entitled signer, as the IAP cover's
   * does.
   */
  repeatable?: boolean;
  /**
   * Roster seats entitled to sign. Unset leaves the box open to anyone who can
   * edit.
   */
  seats?: string[];
};

export type IcsFormVersion = {
  id: string;
  formBaseId: string;
  formInstanceId: string;
  incidentId: string;
  operationalPeriodNumber: number;
  payload: IcsFormVersionPayload;
  signatures: WalaSignature[];
  attachments?: ActionAttachment[];
  formFamily: string;
  createdAt: string;
  updatedAt: string;
  updatedByItemId?: string | null;
  updatedByUserName?: string | null;
};

export type IcsFormVersionTip = IcsFormVersion & {
  signatureSlots: IcsFormSignatureSlot[];
};

const formRoot = (baseId: string, incidentId: string) =>
  `${config.apiGateway.ics_form_version}/${baseId}/incident/${incidentId}`;

const tipKey = (
  baseId: string,
  incidentId: string,
  periodNumber: number,
  formInstanceId?: string
) =>
  [
    'icsFormTip',
    baseId,
    incidentId,
    periodNumber,
    formInstanceId ?? '',
  ] as const;

/**
 * Which chain of a form a request means.
 *
 * Only the 225 sends one. It rates a person rather than a period, so an
 * incident carries several at once and each is signed on its own; every other
 * form has the single chain its period implies, and omits the parameter.
 */
const instanceParams = (formInstanceId?: string) =>
  formInstanceId ? { params: { formInstanceId } } : undefined;

const periodTipsKey = (incidentId: string, periodNumber: number) =>
  ['icsFormTipsForPeriod', incidentId, periodNumber] as const;

const versionsKey = (
  baseId: string,
  incidentId: string,
  formInstanceId: string
) => ['icsFormVersions', baseId, incidentId, formInstanceId] as const;

const toastError = (error: any) =>
  toast.error(
    error?.response?.data?.error?.message || error.message,
    toastConfig
  );

export const useFormVersionTip = (
  baseId: string,
  periodNumber: number,
  formInstanceId?: string
) => {
  const incidentId = useActualIncidentId();
  return useQuery<IcsFormVersionTip | null>({
    queryKey: tipKey(baseId, incidentId ?? '', periodNumber, formInstanceId),
    queryFn: async () => {
      if (!incidentId) return null;
      const { data } = await axios.get(
        `${formRoot(baseId, incidentId)}/period/${periodNumber}/tip`,
        instanceParams(formInstanceId)
      );
      return data;
    },
    enabled: !!incidentId && !!baseId && periodNumber >= 0,
  });
};

export const useSetFormVersionAttachments = (
  baseId: string,
  periodNumber: number
) => {
  const incidentId = useActualIncidentId();
  const invalidate = useVersionInvalidation(baseId, periodNumber);
  return useMutation({
    mutationFn: async (attachments: ActionAttachment[]) => {
      if (!incidentId) return null;
      const { data } = await axios.put(
        `${formRoot(baseId, incidentId)}/period/${periodNumber}/attachments`,
        { attachments }
      );
      return data as IcsFormVersionTip;
    },
    onSuccess: () => invalidate(),
    onError: toastError,
  });
};

/**
 * Existing tips for every versioned IAP-core form in a period. Does not create
 * empty drafts — safe for the Planning-P stepper.
 */
export const useFormVersionTipsForPeriod = (periodNumber: number) => {
  const incidentId = useActualIncidentId();
  return useQuery<IcsFormVersionTip[]>({
    queryKey: periodTipsKey(incidentId ?? '', periodNumber),
    queryFn: async () => {
      if (!incidentId) return [];
      const { data } = await axios.get(
        `${config.apiGateway.ics_form_version}/incident/${incidentId}/period/${periodNumber}/tips`
      );
      return data;
    },
    enabled: !!incidentId && periodNumber >= 0,
  });
};

export const useFormVersions = (baseId: string, formInstanceId?: string) => {
  const incidentId = useActualIncidentId();
  return useQuery<IcsFormVersion[]>({
    queryKey: versionsKey(baseId, incidentId ?? '', formInstanceId ?? ''),
    queryFn: async () => {
      if (!incidentId || !formInstanceId) return [];
      const { data } = await axios.get(
        `${formRoot(baseId, incidentId)}/${formInstanceId}/versions`
      );
      return data;
    },
    enabled: !!incidentId && !!baseId && !!formInstanceId,
  });
};

/** Shared by every mutation: refresh the tip, and the chain it belongs to. */
const useVersionInvalidation = (
  baseId: string,
  periodNumber: number,
  scopedInstanceId?: string
) => {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return (formInstanceId?: string) => {
    queryClient.invalidateQueries({
      queryKey: tipKey(baseId, incidentId, periodNumber, scopedInstanceId),
    });
    queryClient.invalidateQueries({
      queryKey: periodTipsKey(incidentId ?? '', periodNumber),
    });
    if (formInstanceId) {
      queryClient.invalidateQueries({
        queryKey: versionsKey(baseId, incidentId, formInstanceId),
      });
    }
  };
};

const formLabel = (baseId: string) => baseId.replace('ICS_', 'ICS-');

/**
 * A 213RR signature changes the request row, so the list and created resources
 * refresh with it.
 */
const refresh213rrRequest = (
  queryClient: ReturnType<typeof useQueryClient>,
  baseId: string,
  incidentId: string | undefined
) => {
  if (baseId !== 'ICS_213RR' || !incidentId) return;
  queryClient.invalidateQueries({
    queryKey: ['incident_resource_requests', incidentId],
  });
  queryClient.invalidateQueries({ queryKey: ['resource_sets'] });
  queryClient.invalidateQueries({
    queryKey: ['resources-in-incident', incidentId],
  });
};

/**
 * Records a card save as a version. Runs after the card's own write succeeds,
 * so the server snapshots values that are already persisted. Stays quiet on
 * success: the card has already told the user it saved.
 */
export const useRecordFormVersionSave = (
  baseId: string,
  periodNumber: number,
  formInstanceId?: string
) => {
  const incidentId = useActualIncidentId();
  const invalidate = useVersionInvalidation(
    baseId,
    periodNumber,
    formInstanceId
  );
  const forceAppendRef = useRef(false);
  const mutation = useMutation({
    mutationFn: async () => {
      const force = forceAppendRef.current;
      forceAppendRef.current = false;
      return await axios.post(
        `${formRoot(baseId, incidentId)}/period/${periodNumber}/versions/save`,
        force ? { force: true } : undefined,
        instanceParams(formInstanceId)
      );
    },
    onSuccess: (response) => {
      invalidate(response?.data?.formInstanceId as string | undefined);
    },
    onError: toastError,
  });
  return {
    ...mutation,
    /**
     * Append a row even when the frozen snapshot matches. Used by ICS-202
     * generate.
     */
    appendVersion: (options?: {
      onSuccess?: () => void;
      onError?: (error: unknown) => void;
    }) => {
      forceAppendRef.current = true;
      mutation.mutate(undefined, options);
    },
  };
};

export const useCreateSignedFormVersion = (
  baseId: string,
  periodNumber: number,
  formInstanceId?: string
) => {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  const invalidate = useVersionInvalidation(
    baseId,
    periodNumber,
    formInstanceId
  );
  return useMutation({
    mutationFn: async (preparedBy: WalaSignature) =>
      await axios.post(
        `${formRoot(baseId, incidentId)}/period/${periodNumber}/versions/signed`,
        { preparedBy },
        instanceParams(formInstanceId)
      ),
    onSuccess: (response) => {
      invalidate(response?.data?.formInstanceId as string | undefined);
      refresh213rrRequest(queryClient, baseId, incidentId);
      toast.success(
        `Signed ${formLabel(baseId)} version created.`,
        toastConfig
      );
    },
    onError: toastError,
  });
};

export const useCreateUnsignedFormVersion = (
  baseId: string,
  periodNumber: number,
  formInstanceId?: string
) => {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  const invalidate = useVersionInvalidation(
    baseId,
    periodNumber,
    formInstanceId
  );
  return useMutation({
    mutationFn: async () =>
      await axios.post(
        `${formRoot(baseId, incidentId)}/period/${periodNumber}/versions/unsigned`,
        undefined,
        instanceParams(formInstanceId)
      ),
    onSuccess: (response) => {
      invalidate(response?.data?.formInstanceId as string | undefined);
      refresh213rrRequest(queryClient, baseId, incidentId);
      toast.success(`Draft ${formLabel(baseId)} version created.`, toastConfig);
    },
    onError: toastError,
  });
};

/** Drop one role from a 213RR tip and open a new draft tip with the rest. */
export const useRejectFormVersion = (
  baseId: string,
  periodNumber: number,
  formInstanceId?: string
) => {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  const invalidate = useVersionInvalidation(
    baseId,
    periodNumber,
    formInstanceId
  );
  return useMutation({
    mutationFn: async (role: string) =>
      await axios.post(
        `${formRoot(baseId, incidentId)}/period/${periodNumber}/versions/reject`,
        { role },
        instanceParams(formInstanceId)
      ),
    onSuccess: (response) => {
      invalidate(response?.data?.formInstanceId as string | undefined);
      refresh213rrRequest(queryClient, baseId, incidentId);
      toast.success(`${formLabel(baseId)} signature rejected.`, toastConfig);
    },
    onError: toastError,
  });
};

/** Appends an approval to a signed tip: the 202's IC, the 206's Safety Officer. */
export const useAppendFormSignature = (
  baseId: string,
  periodNumber: number,
  formInstanceId?: string
) => {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  const invalidate = useVersionInvalidation(
    baseId,
    periodNumber,
    formInstanceId
  );
  return useMutation({
    mutationFn: async (signature: WalaSignature) =>
      await axios.post(
        `${formRoot(baseId, incidentId)}/period/${periodNumber}/signatures`,
        { signature },
        instanceParams(formInstanceId)
      ),
    onSuccess: (response) => {
      invalidate(response?.data?.formInstanceId as string | undefined);
      refresh213rrRequest(queryClient, baseId, incidentId);
      toast.success('Signature added.', toastConfig);
    },
    onError: toastError,
  });
};
